import { useState, useEffect, useCallback } from "react";
import { InputAdornment, Paper, TextField, Button, TablePagination, IconButton } from "@mui/material";
import { useNavigate } from "react-router-dom";
import styles from "./styles.module.css";
import DataTable from "components/utils/DataTable";
import SearchIcon from "images/svg/search.svg";
import { ReactComponent as DeleteIcon } from "images/svg/delete-02.svg";
import { ReactComponent as StockDamageIcon } from "images/svg/stock-damage.svg";
import { ReactComponent as DraftIcon } from "images/svg/draft.svg";
import { ROUTES } from "const";
import Info from "components/utils/Alert/Info";
import toast from "react-hot-toast";
import StockDamageHelper from "helpers/StockDamageHelper";
import DeleteStockDamageDialog from "components/popups/DeleteStockDamageDialog/DeleteStockDamageDialog";
import SkeletonLoading from "components/utils/Loading/SkeletonLoading";

function StockDamages() {
  const [filterText, setFilterText] = useState("");
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(20);
  const [damages, setDamages] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [rowToDelete, setRowToDelete] = useState(null);

  const navigate = useNavigate();

  const fetchDamages = useCallback(async () => {
    setIsLoading(true);
    try {
      const damagesData = await StockDamageHelper.getSavedStockDamagesFromDB();
      setDamages(damagesData);
    } catch (error) {
      console.error("Failed to fetch damages from local database", error);
      toast.error("Failed to load damage history");
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchDamages();
  }, [fetchDamages]);

  const handleDeleteClick = (row) => {
    setRowToDelete(row);
    setDeleteDialogOpen(true);
  };

  const handleConfirmDelete = async () => {
    if (!rowToDelete) return;

    setIsDeleting(true);
    try {
      const targetKey = rowToDelete?.local_id;
      if (targetKey !== null && targetKey !== undefined) {
        await StockDamageHelper.deleteStockDamageFromDB(targetKey);
        toast.success("Record deleted");
        fetchDamages();
      }
    } catch (error) {
      console.error("Failed to delete damage from local database", error);
      toast.error("Failed to delete record");
    } finally {
      setIsDeleting(false);
      setDeleteDialogOpen(false);
      setRowToDelete(null);
    }
  };

  const headerData = [
    {
      label: "Date",
      id: "date",
      type: "text",
    },
    {
      label: "PLU",
      id: "productId",
      type: "text",
    },
    {
      label: "Item Name",
      id: "name",
      type: "text",
    },
    {
      label: "Cost",
      id: "price",
      type: "floatAmount",
    },
    {
      label: "Quantity",
      id: "qty",
      type: "callback",
      viewRender: (row) => `${row.qty} ${row.unit || "Unit"}`,
    },
    {
      label: "Total",
      id: "payable",
      type: "floatAmount",
    },
    {
      label: "Actions",
      id: "action",
      type: "callback",
      viewRender: (row) => (
        <IconButton size="small" onClick={() => handleDeleteClick(row)} style={{ color: "#7c7c7d" }}>
          <DeleteIcon className={styles.deleteIcon} />
        </IconButton>
      ),
    },
  ];

  const handleSearch = (value) => {
    setFilterText(value);
    setPage(0);
  };

  const handleAddNewDamage = () => {
    navigate(ROUTES.ADD_STOCK_DAMAGE);
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const filteredDamages = damages.filter((d) => {
    const searchStr = filterText.toLowerCase();
    const dateStr = d.date.toLowerCase();
    return (
      dateStr.includes(searchStr) ||
      d.name.toLowerCase().includes(searchStr) ||
      d.productId.toString().includes(searchStr)
    );
  });

  const paginatedDamages = filteredDamages.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

  return (
    <div style={{ padding: "10px 10px 0 10px", height: "100%", boxSizing: "border-box" }}>
      <div className={styles.container}>
        <div className={styles.headerContainer}>
          <div className={styles.header}>
            <h2>Stock Damages</h2>
            <h5>Manage and Track Stock Damages</h5>
          </div>

          <div className={styles.contentWrapper}>
            <TextField
              placeholder="Search"
              size="small"
              className={styles.searchField}
              value={filterText}
              onChange={(e) => handleSearch(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="end">
                    <IconButton edge="end" style={{ padding: "0" }}>
                      <img src={SearchIcon} alt="Search" className={styles.icon} />
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
            <div className={styles.bar}> </div>
            <div className={styles.buttonContainer}>
              <Button onClick={() => navigate(ROUTES.DRAFT_STOCK_DAMAGES)} className={styles.addCustBtn}>
                <DraftIcon className={styles.icon} style={{ width: "16px", height: "16px" }} />
                Drafts
              </Button>
              <Button onClick={handleAddNewDamage} className={styles.addCustBtn}>
                <StockDamageIcon className={styles.icon} />
                Add New Damage
              </Button>
            </div>
          </div>
        </div>

        <div className={styles.tableContainer}>
          {isLoading ? (
            <SkeletonLoading isOpen={true} count={30} height={40} xs={12} sm={6} md={4} lg={4} />
          ) : filteredDamages && filteredDamages.length > 0 ? (
            <Paper
              elevation={0}
              style={{
                display: "flex",
                flexDirection: "column",
                height: "100%",
              }}
            >
              <div style={{ flex: 1, overflowY: "auto" }}>
                <DataTable columns={headerData} rows={paginatedDamages} />
              </div>

              <div className={styles.pagination}>
                <TablePagination
                  rowsPerPageOptions={[10, 20, 50]}
                  component="div"
                  count={filteredDamages.length}
                  rowsPerPage={rowsPerPage}
                  page={page}
                  onPageChange={handleChangePage}
                  onRowsPerPageChange={handleChangeRowsPerPage}
                />
              </div>
            </Paper>
          ) : (
            <Info
              title="No damages to list"
              content="You have no recorded damages. Click 'Add New Damage' to record a damage."
            />
          )}
        </div>
      </div>

      <DeleteStockDamageDialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
        onConfirm={handleConfirmDelete}
        loading={isDeleting}
      />
    </div>
  );
}

export default StockDamages;
